<script lang="ts" setup>
import DataTable from '#components/elements/dataTable/DataTable.vue';
import Dimensions from '#components/elements/dataTable/dimensions/Dimensions.vue';
import { useLanguage } from '#composables/useLanguage';
import { type DataTableInputType } from '#domain/performance/kpi/kpi.query';
import { useUserStore } from '#stores/user.store';
import { type DataTableViewType } from '@carrefour-gcs/shared';
import { computed, toRef } from 'vue';
import ConfigurationButton from './ConfigurationButton.vue';
import ConfigurationPopIn from './ConfigurationPopIn/ConfigurationPopIn.vue';
import ExportButton from './ExportButton.vue';
import { ExportPopIn } from './ExportPopIn';
import ResetButton from './ResetButton.vue';
import { useDataTable } from './composables/useDataTable';
import { useDataTableColumns } from './composables/useDataTableColumns';
import { useDataTableDimensions } from './composables/useDataTableDimensions';
import { useDataTableExport } from './composables/useDataTableExport';
import { useDataTableKpis } from './composables/useDataTableKpis';
import { useDataTableSort } from './composables/useDataTableSort';
import { useDataTableUserConfiguration } from './composables/useDataTableUserConfiguration';
import { DATA_TABLE_PAGE_SIZE } from './constants/dataTablePageSize.constant';

const userStore = useUserStore();

const props = defineProps<{
  dataTableView: DataTableViewType;
  title?: string;
  isExportEnabled?: boolean;
  notif?: string;
  excludeFiltersFromQueryKey?: string[];
  benchmarkQuery?: DataTableInputType['benchmark'];
  benchmarkIcon?: 'carrefour' | 'market';
}>();

const dataTableView = toRef(props, 'dataTableView');

const {
  selectedDimensions,
  dimensionChips,
  areDimensionsFetched,
  availableDimensions,
  disabledDimensions,
  updateSelectedDimensions,
  resetSelectedDimensions,
  isFetchingDimensions
} = useDataTableDimensions({ dataTableView });

const {
  selectedKpis,
  areAvailableKpisFetched,
  availableKpis,
  updateSelectedKpis,
  resetSelectedKpis,
  isFetchingAvailableKpis
} = useDataTableKpis({ dataTableView });

const canFetchColumns = computed(
  () =>
    areDimensionsFetched.value &&
    areAvailableKpisFetched.value &&
    !isFetchingDimensions.value &&
    !isFetchingAvailableKpis.value
);

const { isExportPending, isExportSucceeded, exportDataTable, resetExport } = useDataTableExport({
  dataTableView,
  selectedDimensions,
  selectedKpis,
  benchmarkQuery: toRef(props, 'benchmarkQuery')
});

const {
  isConfigurationPopInOpen,
  isExportPopInOpen,
  closeConfigurationPopIn,
  closeExportPopIn,
  openConfigurationPopIn,
  openExportPopIn,
  configurationKpisColumnsOrder,
  configurationDimensionsOrder,
  updateConfiguration,
  resetConfiguration
} = useDataTableUserConfiguration({
  dataTableView,
  availableKpis,
  availableDimensions,
  updateSelectedKpis,
  updateSelectedDimensions,
  resetExport,
  resetSelectedDimensions,
  resetSelectedKpis
});

const configurationColumnsOrder = computed(() => [
  ...configurationDimensionsOrder.value,
  ...configurationKpisColumnsOrder.value
]);

const { dataTableColumns, columnsDefinition, columnsOrder, fixedColumns, areColumnsFetched, isFetchingColumns } =
  useDataTableColumns({
    dataTableView,
    selectedDimensions,
    selectedKpis,
    configurationColumnsOrder,
    canFetchColumns
  });

const { dataTableSort, sortQueryParams, isSorted } = useDataTableSort({ dataTableColumns });

const canFetchRows = computed(() => canFetchColumns.value && isSorted.value && !isFetchingColumns.value);

const { rows, isLoadingData, loadMore, fetchNextPage } = useDataTable({
  dataTableView,
  selectedDimensions,
  selectedKpis,
  canFetchRows,
  sortQueryParams,
  columnsDefinition,
  paginationDefaultValue: DATA_TABLE_PAGE_SIZE,
  sourceContext: dataTableView.value === 'projectDetail' ? 'projects' : undefined,
  excludeFiltersFromQueryKey: props.excludeFiltersFromQueryKey,
  benchmarkQuery: toRef(props, 'benchmarkQuery'),
  benchmarkIcon: toRef(props, 'benchmarkIcon')
});

const { t } = useLanguage();

const canConfigureAndExportDataTable = computed(() => areDimensionsFetched.value && areColumnsFetched.value);
const requiredDimensions = computed(() => {
  if (availableDimensions.value) {
    return availableDimensions.value.filter((dimension) => dimension.required).map((dimension) => dimension.name);
  }
  return [];
});
</script>

<template>
  <div>
    <div class="flex items-center justify-between gap-4">
      <div class="flex justify-start gap-4 items-center py-6">
        <h1 class="text-2xl font-bold">{{ t(`dataTablePerformance.title`) }}</h1>
        <div v-if="notif">
          <div class="text-xs font-bold text-info bg-background-main rounded p-1">
            {{ notif }}
          </div>
        </div>
      </div>
      <div class="flex items-center gap-4">
        <ResetButton v-if="canConfigureAndExportDataTable" @click="resetConfiguration" />
        <template v-if="isExportEnabled">
          <ExportButton v-if="canConfigureAndExportDataTable" @click="openExportPopIn" />
        </template>
        <ConfigurationButton v-if="canConfigureAndExportDataTable" @click="openConfigurationPopIn" />
      </div>
    </div>
    <div class="pb-6">
      <Dimensions
        :options="dimensionChips"
        :required-options="requiredDimensions"
        @set-selected-dimensions="updateSelectedDimensions"
      />

      <DataTable
        v-model:sort="dataTableSort"
        :columns-definition="columnsDefinition"
        :columns-order="columnsOrder"
        :rows="rows"
        :load-more="loadMore"
        :is-loading="isLoadingData"
        :nb-loading-rows="DATA_TABLE_PAGE_SIZE"
        :fixed-columns="fixedColumns"
        @load-more="fetchNextPage"
      />
    </div>
    <ConfigurationPopIn
      v-if="isConfigurationPopInOpen"
      :datatable-view="dataTableView"
      :is-open="isConfigurationPopInOpen"
      :available-kpis="availableKpis ?? []"
      :selected-kpis="selectedKpis ?? []"
      :available-dimensions="availableDimensions ?? []"
      :selected-dimensions="selectedDimensions ?? []"
      :disabled-dimensions="disabledDimensions"
      :kpis-columns-order="configurationKpisColumnsOrder"
      :dimensions-order="configurationDimensionsOrder"
      @close="closeConfigurationPopIn"
      @update-configuration="updateConfiguration"
    />
    <ExportPopIn
      v-if="isExportPopInOpen"
      :is-open="isExportPopInOpen"
      :is-export-pending="isExportPending"
      :is-export-succeeded="isExportSucceeded"
      :selected-dimensions="selectedDimensions ?? []"
      :user-country="userStore.userCountry"
      :data-table-view="dataTableView"
      @close="closeExportPopIn"
      @open-configuration="openConfigurationPopIn"
      @confirm-export="exportDataTable"
    />
  </div>
</template>
